import React, { useRef, useEffect } from 'react';
import { useDrag, useDrop, XYCoord } from 'react-dnd';
import { Button } from '@edusense/shared-ui-kit';
import { CourseCard } from '@edusense/features-course';
import { DisplayedCourse, mapCourseSummaryToCourseType } from './useRoadmapCreate';
import { DragHandle, SelectedCourseItemSC } from './styles';

const ItemTypes = { COURSE: 'course' };

interface DraggableSelectedCourseItemProps {
  course: DisplayedCourse;
  index: number;
  moveCourse: (dragIndex: number, hoverIndex: number) => void;
  onRemove: (courseId: string) => void;
}

/**
 * 드래그 앤 드롭이 가능한 선택된 강의 아이템 컴포넌트
 * React DnD를 사용하여 강의 순서를 변경할 수 있음
 */
export const DraggableSelectedCourseItem: React.FC<DraggableSelectedCourseItemProps> = ({ 
  course, 
  index, 
  moveCourse, 
  onRemove 
}) => {
  const itemRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLSpanElement>(null);
  
  // Drop zone 설정
  const [, drop] = useDrop({
    accept: ItemTypes.COURSE,
    hover(item: { id: string; index: number }, monitor: any) {
      if (!itemRef.current) return;
      
      const dragIndex = item.index;
      const hoverIndex = index;
      
      // 같은 아이템이면 무시
      if (dragIndex === hoverIndex) return;
      
      // 드래그된 아이템의 중간점 계산
      const hoverBoundingRect = itemRef.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;
      
      // 아래쪽으로 드래그할 때는 중간점을 넘어야 이동
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      // 위쪽으로 드래그할 때는 중간점을 넘어야 이동
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;
      
      // 실제 아이템 이동
      moveCourse(dragIndex, hoverIndex);
      
      // 드래그 아이템의 인덱스 업데이트 (중복 호출 방지)
      item.index = hoverIndex;
    },
  });
  
  // Drag source 설정
  const [{ isDragging }, drag, preview] = useDrag({
    type: ItemTypes.COURSE,
    item: { id: course.id, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  
  // Refs 연결
  drag(handleRef); // 드래그 핸들에만 드래그 기능 적용
  drop(itemRef);   // 전체 아이템을 드롭 존으로 설정
  
  // 프리뷰 설정
  useEffect(() => {
    if (preview) preview(itemRef);
  }, [preview]);
  
  // CourseCard용 데이터 변환
  const courseCardData = mapCourseSummaryToCourseType(course.originalSummary);
  
  return (
    <SelectedCourseItemSC
      ref={itemRef}
      isDragging={isDragging}
      data-testid={`selected-course-item-${course.id}`}
    >
      {/* 순서 표시 */}
      <span className="order-overlay">{index + 1}</span>
      
      {/* 드래그 핸들 */}
      <DragHandle ref={handleRef} className="drag-handle-overlay" title="드래그하여 순서 변경">
        ≡
      </DragHandle>
      
      {/* 강의 카드 */}
      <div className="selected-course-card-container">
        <CourseCard course={courseCardData} />
      </div>
      
      {/* 제거 버튼 */}
      <Button 
        size="sm" 
        variant="outline" 
        onClick={() => onRemove(course.id)} 
        className="remove-button-overlay" 
        title="이 강의를 로드맵에서 제거"
      >
        제거
      </Button>
    </SelectedCourseItemSC>
  );
};